import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Sparkles } from "lucide-react";

/**
 * FeaturedHero
 * ------------
 * THE collection banner. The featured product's photograph fills the entire
 * banner; an elegant overlay carries the category title + product name + CTA.
 *
 * Behaviour:
 *  - Auto-pulls the manually-featured product, falling back to the newest in
 *    that category (or site-wide when no `category` is provided).
 *  - When the heading prop is supplied, renders the FULL-BLEED banner (used as
 *    the page hero on /shop/:category).
 *  - When no heading is provided, renders a softer "spotlight" card (used on
 *    the homepage so it doesn't compete with the welcome hero).
 *
 * Props:
 *  - category (optional)   "sarees" | "kurtis" | "dresses" | "lehengas".
 *  - heading  (optional)   The category title overlay (e.g. "Sarees").
 */
export default function FeaturedHero({ category, heading, eyebrow: eyebrowOverride, description }) {
  const [product, setProduct] = useState(null);

  useEffect(() => {
    const params = category ? `?category=${category}` : "";
    let cancelled = false;
    api.get(`/products/featured${params}`)
      .then(({ data }) => { if (!cancelled) setProduct(data); })
      .catch((err) => {
        if (err?.response?.status !== 404) {
          console.error("Failed to load featured product:", err);
        }
      });
    return () => { cancelled = true; };
  }, [category]);

  if (!product) return null;

  // Display labels for category slugs (kept separate from the URL/DB key so we
  // can rename labels without breaking links or data).
  const CATEGORY_LABELS = {
    sarees: "Saree",
    kurtis: "Kurti",
    dresses: "Dress",
    lehengas: "Party Wear Piece",
  };

  const isDM = !product.price || product.price === 0;
  const priceLabel = isDM ? "DM for Price" : `₹${Number(product.price).toLocaleString("en-IN")}`;
  const eyebrow = eyebrowOverride
    || (category ? `The ${CATEGORY_LABELS[category] || category} of the Moment` : "Featured This Week");

  // ---------------------------------------------------------------------------
  // Banner mode — the product photograph IS the collection banner.
  // ---------------------------------------------------------------------------
  if (heading) {
    return (
      <section
        className="relative h-[78vh] min-h-[520px] md:h-[88vh] md:min-h-[640px] overflow-hidden"
        data-testid={category ? `featured-hero-${category}` : "featured-hero-home"}
      >
        <img
          src={product.images?.[0]}
          alt={product.name}
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        {/* Soft left-side gradient so overlay text stays readable on any photo */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#FDFBF7] via-[#FDFBF7]/60 to-transparent md:from-[#FDFBF7]/95 md:via-[#FDFBF7]/40" />
        {/* Bottom fade so the banner blends into the next section */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#FDFBF7] to-transparent" />

        <div className="relative h-full flex items-end md:items-center px-6 md:px-12 lg:px-24 pb-12 md:pb-0">
          <div className="max-w-xl" data-testid={`hero-overlay-${category || "home"}`}>
            <div className="text-[11px] tracking-overline uppercase text-[#D4AF37] mb-3">The Collection</div>
            <h1 className="font-serif-h font-light text-5xl md:text-6xl lg:text-7xl text-stone-900 leading-[0.95]" data-testid={`hero-heading-${category || "home"}`}>
              {heading}
            </h1>
            {description && (
              <p className="text-sm md:text-base text-stone-700 mt-5 leading-relaxed max-w-md" data-testid={`hero-description-${category || "home"}`}>
                {description}
              </p>
            )}

            <div className="mt-8 flex items-center gap-2 text-[11px] tracking-overline uppercase text-[#D4AF37]">
              <Sparkles size={14} strokeWidth={1.5} />
              {eyebrow}
            </div>
            <div className="font-serif-h text-xl md:text-2xl text-stone-900 mt-3 leading-snug">
              {product.name}
            </div>
            <div className={`text-base mt-2 font-medium ${isDM ? "text-[#25D366]" : "text-stone-900"}`}>
              {priceLabel}
            </div>

            <div className="mt-7">
              <Link
                to={`/product/${product.id}`}
                className="btn-ink"
                data-testid={`featured-hero-cta-${product.id}`}
              >
                Discover the piece
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ---------------------------------------------------------------------------
  // Spotlight mode — softer card for homepage (doesn't compete with welcome hero).
  // ---------------------------------------------------------------------------
  return (
    <section
      className="relative overflow-hidden bg-gradient-to-br from-[#F8EDE9] via-[#FDFBF7] to-[#F3EAE8] py-14 md:py-20"
      data-testid="featured-hero-home"
    >
      <div className="pointer-events-none absolute inset-0 opacity-[0.08]" aria-hidden="true"
           style={{ backgroundImage: "radial-gradient(circle at 30% 20%, #D4AF37 0%, transparent 60%), radial-gradient(circle at 80% 70%, #5A1618 0%, transparent 70%)" }} />

      <div className="relative px-6 md:px-12 lg:px-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-5">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-overline uppercase text-[#D4AF37]">
            <Sparkles size={14} strokeWidth={1.5} />
            {eyebrow}
          </div>
          <h2 className="font-serif-h font-light text-3xl md:text-4xl lg:text-5xl text-stone-900 mt-4 leading-tight">
            {product.name}
          </h2>
          <p className="text-sm md:text-base text-stone-600 mt-4 leading-relaxed line-clamp-4">
            {product.description}
          </p>
          <div className="flex items-center gap-5 mt-6">
            <span className={`text-lg ${isDM ? "text-[#25D366]" : "text-stone-900"} font-medium`}>
              {priceLabel}
            </span>
          </div>
          <div className="flex flex-wrap gap-3 mt-7">
            <Link
              to={`/product/${product.id}`}
              className="btn-ink"
              data-testid={`featured-hero-cta-${product.id}`}
            >
              Discover the piece
            </Link>
          </div>
        </div>

        <div className="md:col-span-7">
          <div className="relative mx-auto max-w-xl">
            <div className="absolute -inset-3 md:-inset-5 border border-[#D4AF37]/40 pointer-events-none" aria-hidden="true" />
            <div className="relative aspect-[4/5] bg-white shadow-[0_20px_60px_rgba(90,22,24,0.15)]">
              <img
                src={product.images?.[0]}
                alt={product.name}
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute left-6 right-6 bottom-4 bg-white/90 backdrop-blur-sm border border-[#D4AF37]/50 px-4 py-2.5 text-center">
                <div className="font-serif-h text-sm md:text-base text-stone-900 leading-snug">
                  {product.name}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
